import axios from "axios";
import toast from "react-hot-toast";

const API_BASE_URL = import.meta.env.VITE_API_URL || import.meta.env.VITE_NEXT_PUBLIC_API_URL || "http://localhost:4200";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// List of public endpoints that should never include auth token
const publicEndpoints = [
  "/api/v1/hero/featured-collections",
  "/api/v1/products/best-selling",
  "/api/v1/collections",
  "/api/v1/reviews/featured",
  "/api/v1/products",
];

// Helper function to check if URL is a public endpoint
const isPublicEndpoint = (url: string): boolean => {
  return publicEndpoints.some((endpoint) => {
    // Check if URL starts with the endpoint (handles query params)
    if (url.startsWith(endpoint)) {
      return true;
    }
    // Also check without leading slash
    if (url.startsWith(endpoint.substring(1))) {
      return true;
    }
    return false;
  });
};

// Add request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    // Skip adding token for public endpoints
    if (config.url && isPublicEndpoint(config.url)) {
      return config;
    }

    // Skip if explicitly marked to skip (e.g., from token refresh retry)
    if ((config as any).__skipTokenInterceptor) {
      return config;
    }

    // If Authorization header is already set (e.g., from token refresh retry), don't overwrite it
    if (config.headers.Authorization) {
      return config;
    }

    // Get token from localStorage (via zustand store)
    if (typeof window !== "undefined") {
      const authData = localStorage.getItem("aarsa-auth");
      if (authData) {
        try {
          const parsed = JSON.parse(authData);
          if (parsed.state?.token) {
            config.headers.Authorization = `Bearer ${parsed.state.token}`;
          }
        } catch (e) {
          // Ignore parse errors
        }
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token refresh

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // If error is 401, logout user
    if (error.response?.status === 401) {
      if (typeof window !== "undefined") {
        const authStore = await import("./store");
        authStore.useAuthStore.getState().logout();
        toast.error("Your session has expired. Please log in again.");
      }
    }
    return Promise.reject(error);
  }
);

// Products API
export const productsApi = {
  getAll: async (params?: {
    page?: number;
    limit?: number;
    category?: string;
    classification?: string;
    collection?: string;
    search?: string;
    minPrice?: number;
    maxPrice?: number;
  }) => {
    const response = await api.get("/api/v1/products", { params });
    // Backend wraps response in: { success, message, data: { total, products } }
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  getById: async (id: number) => {
    const response = await api.get(`/api/v1/products/${id}`);
    return response.data;
  },
  getBySlug: async (slug: string) => {
    const response = await api.get(`/api/v1/products/${slug}`);
    // Backend wraps response in: { success, message, data: {...} }
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  getBestSelling: async (limit: number = 10) => {
    const response = await api.get("/api/v1/products/best-selling", {
      params: { limit },
    });
    // Backend wraps response in: { success, message, data: { total, products } }
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  getFastSelling: async () => {
    // Note: This endpoint may need to be implemented in the backend
    // For now, using the main products endpoint
    const response = await api.get("/api/v1/products", { params: { page_size: 10 } });
    return response.data;
  },
  getByClassification: async (classification: string) => {
    const response = await api.get(`/api/v1/products/classification/${classification}`);
    return response.data;
  },
};

// Cart API
export const cartApi = {
  get: async (token?: string) => {
    const response = await api.get("/api/v1/cart", {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data;
  },
  addItem: async (item: any, token?: string) => {
    const response = await api.post(
      "/api/v1/cart",
      item,
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );
    return response.data;
  },
  updateItem: async (itemId: string, quantity: number, token?: string) => {
    const response = await api.put(
      `/api/v1/cart/${itemId}`,
      { quantity },
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );
    return response.data;
  },
  removeItem: async (itemId: string, token?: string) => {
    const response = await api.delete(`/api/v1/cart/${itemId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data;
  },
  clear: async (token?: string) => {
    const response = await api.delete("/api/v1/cart", {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data;
  },
};

// Auth API
export const authApi = {
  login: async (email: string, password: string) => {
    const response = await api.post("/api/v1/auth/login", {
      email,
      password,
    });
    
    // Backend wraps response in: { success, message, data: { access_token, token_type, user } }
    // Extract the data field from the wrapped response
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return {
        ...wrappedResponse.data,
        message: wrappedResponse.message || "Login successful",
      };
    }
    // Fallback to direct response if not wrapped
    return wrappedResponse;
  },
  register: async (data: any) => {
    const response = await api.post("/api/v1/auth/register", data);
    return response.data;
  },
  getMe: async (token: string) => {
    const response = await api.get("/api/v1/auth/me", {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
  sendOTP: async (identifier: string, isPhone: boolean = false) => {
    const response = await api.post("/api/v1/auth/send-otp", {
      identifier,
      is_phone: isPhone,
    });
    return response.data;
  },
  verifyOTP: async (identifier: string, otpCode: string, isPhone: boolean = false) => {
    const response = await api.post("/api/v1/auth/verify-otp", {
      identifier,
      otp_code: otpCode,
      is_phone: isPhone,
    });
    // Backend wraps response in: { success, message, data: {...} }
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return {
        ...wrappedResponse.data,
        message: wrappedResponse.message || "Login successful",
      };
    }
    return wrappedResponse;
  },
};

// Wishlist API
export const wishlistApi = {
  getAll: async (token: string) => {
    const response = await api.get("/api/v1/wishlist", {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
  add: async (productId: number, token: string) => {
    const response = await api.post(
      "/api/v1/wishlist/add",
      { product_id: productId },
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response.data;
  },
  remove: async (productId: number, token: string) => {
    const response = await api.delete(`/api/v1/wishlist/remove/${productId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
  check: async (productId: number, token: string) => {
    const response = await api.get(`/api/v1/wishlist/check/${productId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
};

// Orders API
export const ordersApi = {
  getAll: async (token: string) => {
    const response = await api.get("/api/v1/orders", {
      headers: { Authorization: `Bearer ${token}` },
    });
    // Backend wraps response in: { success, message, data: { total, orders } }
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  getById: async (orderId: number, token: string) => {
    const response = await api.get(`/api/v1/orders/${orderId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
  cancel: async (orderId: number, token: string, reason?: string) => {
    const response = await api.post(
      `/api/v1/orders/${orderId}/cancel`,
      { reason },
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response.data;
  },
  requestRefund: async (orderId: number, token: string, reason?: string) => {
    const response = await api.post(
      `/api/v1/orders/${orderId}/refund`,
      { reason },
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response.data;
  },
};

// Reviews API
export const reviewsApi = {
  create: async (review: {
    product_id: number;
    order_id?: number;
    rating: number;
    title?: string;
    comment?: string;
  }, token: string) => {
    const response = await api.post("/api/v1/reviews", review, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
  update: async (reviewId: number, review: {
    rating?: number;
    title?: string;
    comment?: string;
  }, token: string) => {
    const response = await api.put(`/api/v1/reviews/${reviewId}`, review, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
  getByProduct: async (productId: number) => {
    const response = await api.get(`/api/v1/reviews/product/${productId}`);
    return response.data;
  },
  getByUser: async (token: string) => {
    const response = await api.get("/api/v1/reviews/me", {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
};

// Collections API
export const collectionsApi = {
  getAll: async () => {
    const response = await api.get("/api/v1/collections");
    // Backend wraps response in: { success, message, data: { total, collections } }
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
};

// Categories API
export const categoriesApi = {
  getAll: async () => {
    const response = await api.get("/api/v1/categories");
    // Backend wraps response in: { success, message, data: { total, categories } }
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
};

// Customer Reviews API
export const customerReviewsApi = {
  getFeatured: async (limit: number = 6) => {
    const response = await api.get("/api/v1/reviews/featured", {
      params: { limit },
    });
    // Backend wraps response in: { success, message, data: { total, reviews } }
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
};

// Hero Featured Collections API
export const heroApi = {
  getFeaturedCollections: async () => {
    const response = await api.get("/api/v1/hero/featured-collections");
    // Backend wraps response in: { success, message, data: { total, collections } }
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
};

// Admin API
export const adminApi = {
  getDashboardStats: async (token: string) => {
    const response = await api.get("/api/v1/admin/dashboard", {
      headers: { Authorization: `Bearer ${token}` },
    });
    // Backend wraps response in: { success, message, data: { ... } }
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  // Products
  getProducts: async (token: string, params?: any) => {
    const response = await api.get("/api/v1/admin/products", {
      headers: { Authorization: `Bearer ${token}` },
      params,
    });
    // Backend wraps response in: { success, message, data: { total, products } }
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  getProduct: async (productId: number, token: string) => {
    const response = await api.get(`/api/v1/admin/products/${productId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    // Backend wraps response in: { success, message, data: { ... } }
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  createProduct: async (product: any, token: string) => {
    const response = await api.post("/api/v1/admin/products", product, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
  updateProduct: async (productId: number, product: any, token: string) => {
    const response = await api.put(`/api/v1/admin/products/${productId}`, product, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
  deleteProduct: async (productId: number, token: string) => {
    const response = await api.delete(`/api/v1/admin/products/${productId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
  // Inventory
  getInventory: async (token: string, params?: any) => {
    const response = await api.get("/api/v1/admin/inventory", {
      headers: { Authorization: `Bearer ${token}` },
      params,
    });
    return response.data;
  },
  updateInventory: async (productId: number, inventory: any, token: string) => {
    const response = await api.put(`/api/v1/admin/inventory/${productId}`, inventory, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
  // Orders
  getOrders: async (token: string, params?: any) => {
    const response = await api.get("/api/v1/admin/orders", {
      headers: { Authorization: `Bearer ${token}` },
      params,
    });
    // Backend wraps response in: { success, message, data: { total, orders } }
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  getOrder: async (orderId: number, token: string) => {
    const response = await api.get(`/api/v1/admin/orders/${orderId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    // Backend wraps response in: { success, message, data: { ... } }
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  updateOrderStatus: async (orderId: number, status: string, token: string) => {
    const response = await api.put(
      `/api/v1/admin/orders/${orderId}/status`,
      { status },
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response.data;
  },
  // Payments
  getPayments: async (token: string, params?: any) => {
    const response = await api.get("/api/v1/admin/payments", {
      headers: { Authorization: `Bearer ${token}` },
      params,
    });
    // Backend wraps response in: { success, message, data: { total, payments } }
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  // Users
  getUsers: async (token: string, params?: any) => {
    const response = await api.get("/api/v1/admin/users", {
      headers: { Authorization: `Bearer ${token}` },
      params,
    });
    // Backend wraps response in: { success, message, data: { total, users } }
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  // Wishlist
  getWishlistItems: async (token: string, params?: any) => {
    const response = await api.get("/api/v1/admin/wishlist", {
      headers: { Authorization: `Bearer ${token}` },
      params,
    });
    // Backend wraps response in: { success, message, data: { total, wishlist_items } }
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
};

// Storage API
export const storageApi = {
  getUploadUrl: async (data: {
    file_name: string;
    file_type: string;
    file_size: number;
    product_id?: number | null;
  }) => {
    const response = await api.post("/api/v1/storage/upload-url", data);
    const wrappedResponse = response.data;
    if (wrappedResponse.data) {
      return wrappedResponse.data;
    }
    return wrappedResponse;
  },
  uploadComplete: async (data: {
    s3_key: string;
    product_id?: number | null;
    alt_text?: string | null;
  }) => {
    const response = await api.post("/api/v1/storage/upload-complete", data);
    return response.data;
  },
  deleteFile: async (s3_key: string) => {
    const response = await api.delete(`/api/v1/storage/${encodeURIComponent(s3_key)}`);
    return response.data;
  },
};

export default api;

